// Job Share Button - share menu shared between the job detail panels
// (Jobs / PublicJobs) and the facility Job Description modal.
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Pressable } from 'react-native';
import { Share2, Link2, Send, Mail, Check } from 'lucide-react';
import { jobDetailStyles as styles } from './jobStyles';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { buildJobShareMessage } from '../../../utils/jobShare';

/**
 * @param {Object} props
 * @param {Object} props.job - Job object; the share text carries its clinic,
 *   schedule, location, contact number and description
 * @param {string} [props.shareUrl] - URL to share (defaults to current page URL)
 * @param {Object} [props.buttonStyle] - Overrides the default (primary) button style
 * @param {Object} [props.buttonTextStyle] - Overrides the default button label style
 * @param {string} [props.iconColor] - Share icon colour (defaults to white)
 * @param {Object} [props.menuStyle] - Extra styles for the dropdown (e.g. right-aligning it)
 */
const JobShareButton = ({
  job,
  shareUrl,
  buttonStyle,
  buttonTextStyle,
  iconColor = '#ffffff',
  menuStyle,
}) => {
  const isMobile = useIsMobile();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const showToastNotification = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fallbackCopyToClipboard = (content) => {
    if (typeof document === 'undefined') return;
    const textArea = document.createElement('textarea');
    textArea.value = content;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      showToastNotification();
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }
    document.body.removeChild(textArea);
  };

  // Share handlers
  const handleShareVia = (platform) => {
    const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const title = job?.title || 'Job Opportunity';
    const text = buildJobShareMessage(job, job?.uuid, { url });

    switch (platform) {
      case 'copy': {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
          navigator.clipboard.writeText(text).then(() => {
            showToastNotification();
          }).catch(() => {
            fallbackCopyToClipboard(text);
          });
        } else {
          fallbackCopyToClipboard(text);
        }
        break;
      }
      case 'whatsapp':
        if (typeof window !== 'undefined') {
          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        }
        break;
      case 'telegram':
        if (typeof window !== 'undefined') {
          // Telegram appends the url itself, so the blurb goes in without it.
          const telegramText = buildJobShareMessage(job, job?.uuid, { url, includeLink: false });
          window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(telegramText)}`, '_blank');
        }
        break;
      case 'email':
        if (typeof window !== 'undefined') {
          window.location.href = `mailto:?subject=${encodeURIComponent('Job Opportunity: ' + title)}&body=${encodeURIComponent(text)}`;
        }
        break;
      case 'facebook':
        if (typeof window !== 'undefined') {
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        }
        break;
      case 'linkedin':
        if (typeof window !== 'undefined') {
          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        }
        break;
      case 'viber': {
        if (typeof window !== 'undefined') {
          const viberText = encodeURIComponent(text);
          const viberDeepLink = `viber://forward?text=${viberText}`;
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = viberDeepLink;
          document.body.appendChild(iframe);
          const fallbackTimeout = setTimeout(() => {
            window.open('https://www.viber.com/', '_blank');
          }, 1500);
          window.addEventListener('blur', function onBlur() {
            clearTimeout(fallbackTimeout);
            window.removeEventListener('blur', onBlur);
          });
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 2000);
        }
        break;
      }
    }
    setShowShareMenu(false);
  };

  return (
    <View style={styles.shareButtonWrapper}>
      <TouchableOpacity
        style={buttonStyle || styles.applyBtn}
        onPress={() => setShowShareMenu(!showShareMenu)}
      >
        <View style={styles.shareButtonContent}>
          <Share2 size={16} color={iconColor} />
          <Text style={buttonTextStyle || styles.applyBtnText}>Share</Text>
        </View>
      </TouchableOpacity>

      {/* Share Menu Dropdown */}
      {showShareMenu && (
        <>
          <Pressable
            style={styles.shareBackdrop}
            onPress={() => setShowShareMenu(false)}
          />
          <View style={[styles.shareMenuInline, menuStyle, isMobile && styles.shareMenuMobile]}>
            {!isMobile && <View style={styles.shareMenuArrowInline} />}
            <View style={styles.shareMenuHeader}>
              <Text style={styles.shareMenuTitle}>Share via</Text>
            </View>
            <View style={styles.shareOptionsRow}>
              {/* Copy Link */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('copy')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconCopy]}>
                  <Link2 size={20} color="#333333" />
                </View>
                <Text style={styles.shareOptionText}>Website</Text>
              </TouchableOpacity>

              {/* WhatsApp */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('whatsapp')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconWhatsApp]}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" style={{ pointerEvents: 'none' }}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </View>
                <Text style={styles.shareOptionText}>WhatsApp</Text>
              </TouchableOpacity>

              {/* Telegram */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('telegram')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconTelegram]}>
                  <Send size={20} color="#ffffff" />
                </View>
                <Text style={styles.shareOptionText}>Telegram</Text>
              </TouchableOpacity>

              {/* Email */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('email')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconEmail]}>
                  <Mail size={20} color="#ffffff" />
                </View>
                <Text style={styles.shareOptionText}>Email</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.shareOptionsRow}>
              {/* Facebook */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('facebook')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconFacebook]}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" style={{ pointerEvents: 'none' }}>
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </View>
                <Text style={styles.shareOptionText}>Facebook</Text>
              </TouchableOpacity>

              {/* LinkedIn */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('linkedin')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconLinkedIn]}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" style={{ pointerEvents: 'none' }}>
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </View>
                <Text style={styles.shareOptionText}>LinkedIn</Text>
              </TouchableOpacity>

              {/* Viber */}
              <TouchableOpacity
                style={styles.shareOption}
                onPress={() => handleShareVia('viber')}
              >
                <View style={[styles.shareOptionIcon, styles.shareIconViber]}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" style={{ pointerEvents: 'none' }}>
                    <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.177.518 6.769.399 9.932c-.12 3.163-.27 9.09 5.566 10.728l.004.002-.002 2.458s-.038.993.617 1.196c.792.244 1.258-.51 2.016-1.324.417-.446.99-1.1 1.424-1.6 3.922.33 6.935-.424 7.278-.538.793-.263 5.279-.833 6.011-6.799.755-6.154-.358-10.04-2.34-11.793C19.074.86 15.43-.048 11.398.002zm.297 2.116c3.527-.064 6.666.665 8.322 2.172 1.621 1.47 2.563 4.852 1.903 10.207-.584 4.745-4.005 5.327-4.678 5.55-.28.093-2.886.749-6.226.545 0 0-2.468 2.978-3.24 3.757-.12.122-.263.17-.358.148-.133-.032-.17-.188-.168-.415l.022-4.08c-4.71-1.32-4.436-6.14-4.335-8.836.1-2.695.726-4.908 2.148-6.318 1.674-1.595 4.963-2.448 8.128-2.56.162-.005.322-.005.482-.006v.036zm-.22 2.823c-.09 0-.09.137 0 .142 1.26.065 2.394.416 3.298 1.15.891.73 1.483 1.748 1.708 3.04.014.084.152.074.14-.012-.483-2.85-2.498-4.254-5.133-4.318-.004 0-.009-.002-.013-.002zm-4.85 1.178c-.321-.005-.65.106-.88.37l-.002-.001c-.337.384-.354.86-.223 1.31.253.905.899 2.086 1.794 2.943l.007.013.012.007c.76.72 2.027 1.496 3.002 1.87.467.18.935.164 1.305-.15l-.001-.003c.372-.33.544-.77.413-1.193-.13-.417-.589-.847-.972-1.101-.53-.35-1.056-.21-1.355.146l-.344.396c-.27.296-.705.2-.705.2-1.626-.443-2.615-1.874-2.615-1.874s-.165-.406.132-.676l.396-.344c.358-.299.498-.826.148-1.356-.254-.383-.684-.84-1.1-.97a1.184 1.184 0 00-.406-.058l.003-.001.003.002-.003.002.003-.002-.003.002zm5.636.888c-.087.007-.078.146.01.144a3.427 3.427 0 012.403 1.388c.524.734.73 1.517.668 2.462-.006.088.13.098.14.012.215-1.96-.658-3.677-3.18-3.999a.093.093 0 00-.04-.007zm.072 1.69c-.086-.005-.1.117-.015.135.73.153 1.193.637 1.316 1.418.012.083.146.072.137-.012-.099-.86-.53-1.41-1.41-1.539a.128.128 0 00-.028-.003z" />
                  </svg>
                </View>
                <Text style={styles.shareOptionText}>Viber</Text>
              </TouchableOpacity>
            </View>
          </View>
        </>
      )}

      {/* Toast Notification */}
      {showToast && (
        <View style={styles.toast}>
          <Check size={18} color="#4ade80" />
          <Text style={styles.toastText}>Job details copied to clipboard!</Text>
        </View>
      )}
    </View>
  );
};

export default JobShareButton;
